import { CSSProperties, useEffect, useState } from 'react';
import useMessageYourFans from 'Hooks/useMessageYourFans';
import { getFanbase_api } from 'api/brandApi';
import { BrandExistsState } from 'Hooks/useCurrentBrand';
import useCurrentBrand from 'Hooks/useCurrentBrand';
import { styx, clsx } from 'modules/Utils';
import Colors from 'modules/Colors';
import CardSymphony from 'elements/CardSymphony';
import { ScheduleOptions } from 'modules/Types';
import Icon from 'elements/Icon';
import DebugBox from 'components/shareable/DebugBox';
import dayjs from 'dayjs';

/** Monthly free credits allocated to each user */
const FREE_MONTHLY_CREDITS = 1000;
/** Cost per email after free credits are exhausted */
const COST_PER_EMAIL = 0.10;

interface ConfirmationTabProps {
    className?: string;
    style?: CSSProperties;
}

const ConfirmationTab = ({
    className,
    style
}: ConfirmationTabProps) => {
    const { currentBrand } = useCurrentBrand() as BrandExistsState;
    const { details, setDetails: saveDetails, getEmailUsage } = useMessageYourFans();
    const { date, timezone, fans, scheduled } = details;

    const emailCount = fans.filter(fan => fan.email).length;
    const [creditsUsed, setCreditsUsed] = useState(0);
    const [isLoadingFanbase, setIsLoadingFanbase] = useState(false);

    /** Calculate billable credits after free monthly allocation */
    const billableCredits = Math.max(0, emailCount + creditsUsed - FREE_MONTHLY_CREDITS);
    /** Calculate cost based only on credits exceeding free allocation */
    const emailCost = billableCredits * COST_PER_EMAIL;

    const getFanbase = async () => {
        {
            try {
                setIsLoadingFanbase(true);
                const emailUsage = await getEmailUsage();
                setCreditsUsed(emailUsage.usage);

                const { fanbase } = await getFanbase_api(currentBrand.id, { subscribed: true, limit: Infinity });
                if (!!fanbase) {
                    saveDetails({ ...details, fans: fanbase });
                }
            } catch (error) {
                console.error('Error getting email usage:', error);
                throw error;
            } finally {
                setIsLoadingFanbase(false);
            }
        }
    }

    useEffect(() => {
        getFanbase()
    }, [currentBrand.id]);

    return (
        <div
            className={clsx('messageYourFansFinalizeContainer', className)}
            style={styx(sty.messageYourFansFinalizeContainer, style)}
        >
            <CardSymphony
                className="confirmationTabConfirmationHeader"
                title="Confirmation"
                subtitle="Review the details of your campaign"
                size="small"
            />

            <CardSymphony
                className="confirmationTabScheduleHeader"
                title="Scheduled"
                subtitle={
                    <div className="confirmationSubtitle" style={sty.confirmationSubtitle}>
                        <Icon className="calendarIcon" icon="calendar" isCircle />
                        <span style={sty.scheduleText}>
                            {scheduled === ScheduleOptions.SCHEDULED ?
                                `${dayjs(date).format('MMMM Do, YYYY, hh:mm a')} - ${timezone.title}`
                                :
                                'ASAP'
                            }
                        </span>
                    </div>
                }
                size="small"
            />

            <CardSymphony
                className="confirmationTabMessagesHeader"
                size="small"
                title="Messages"
            >
                <div className="messagesSection">
                    <div className="creditsUsedRow" style={styx(sty.confirmationSubtitle, sty.creditsUsedRow)}>
                        <Icon className="pieChartIcon" icon="pieChart" size={20} />
                        You'll use<span style={sty.boldText}>{emailCount.toLocaleString()}</span>of your {FREE_MONTHLY_CREDITS.toLocaleString()} credits
                    </div>
                    <div 
                        className="messagesUsedRow" 
                        style={styx(sty.messagesUsedRow, sty.confirmationSubtitle)}
                    >
                        <div style={sty.messageRowLeft}>
                            <Icon className="messageIcon" icon="mail" isCircle />
                            <div><span style={sty.boldText}>{emailCount}</span> Email messages</div>
                        </div>
                        <div><span style={sty.boldText}>{emailCount}</span> credits</div>
                        <div><span style={sty.boldText}>${emailCost.toFixed(2)}</span> USD</div>
                    </div>
                    <div className="confirmationSubtitle" style={styx(sty.confirmationSubtitle, sty.totalCostRow)}>
                        <div>Total, this will be charged at the end of your billing period.</div>
                        <div><span style={sty.boldText}>${emailCost.toFixed(2)}</span> USD</div>
                    </div>
                </div>
            </CardSymphony>

        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    boldText: {
        fontWeight: 600,
        display: 'inline',
        margin: 0,
        padding: 0
    },
    calendarIcon: {
        backgroundColor: Colors.purpleLighter,
        borderRadius: '50%',
        flexShrink: 0,
        height: 34,
        padding: 8,
        width: 34
    },
    creditsUsedRow: {
        alignItems: 'center',
        backgroundColor: Colors.purpleLighter,
        borderRadius: 8,
        color: Colors.black,
        display: 'flex',
        fontSize: 16,
        gap: 8,
        padding: 16,
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
    },
    confirmationSubtitle: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
    },
    messageIcon: {
        backgroundColor: Colors.purpleLighter,
        borderRadius: '50%',
        height: 34,
        padding: 0,
        width: 34
    },
    messageRowLeft: {
        alignItems: 'center',
        display: 'flex',
        gap: 8
    },
    messagesUsedRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 8,
        paddingLeft: 0,
        paddingRight: 16,
        paddingTop: 16,
        width: '100%'
    },
    messageYourFansFinalizeContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 680,
        width: '100%'
    },
    pieChartIcon: {
        height: 20,
        width: 20
    },
    scheduleText: {
        display: 'inline-block'
    },
    totalCostRow: {
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
        color: Colors.black,
        display: 'flex',
        justifyContent: 'space-between',
        padding: 16,
        paddingTop: 16,
        paddingRight: 16,
        paddingBottom: 12,
        width: '100%'
    }
};

export default ConfirmationTab; 